import { catalogs } from "./catalogs/index";

/** All valid translation key paths */
export type TranslationKey =
  | "labels.cardNumber"
  | "labels.expirationDate"
  | "labels.cvc"
  | "placeholders.cardNumber"
  | "placeholders.expirationDate"
  | "placeholders.cvc"
  | "errors.invalidCardNumber"
  | "errors.invalidExpiryDate"
  | "errors.invalidCvc"
  | "errors.incompleteCardNumber"
  | "errors.incompleteExpiryDate"
  | "errors.incompleteCvc"
  | "errors.expiredCard"
  | "aria.cardNumber"
  | "aria.expirationDate"
  | "aria.cvc"
  | "aria.cardInput";

/** A complete translation catalog: every key maps to a non-empty string */
export type TranslationCatalog = Record<TranslationKey, string>;

/** Merchant-provided partial overrides */
export type TranslationOverride = Partial<Record<string, unknown>>;

/** Supported base locale codes */
export type SupportedLocale =
  | "en"
  | "fr"
  | "de"
  | "es"
  | "pt"
  | "it"
  | "nl"
  | "ja"
  | "zh"
  | "ar"
  | "hi"
  | "bn"
  | "ru"
  | "ko"
  | "vi"
  | "tr"
  | "th"
  | "pl"
  | "uk"
  | "ms"
  | "tl"
  | "sw"
  | "ta"
  | "he"
  | "id";

export interface TranslationManager {
  /**
   * Initialise with a locale and optional merchant overrides.
   * Resolves the base language from a BCP 47 tag, loads the catalog,
   * merges overrides, and logs a warning if falling back to English.
   */
  init(locale: string, overrides?: TranslationOverride): void;

  /**
   * Resolve a translation key to a localised string.
   * Lookup order: override → active locale catalog → English fallback.
   * Always returns a non-empty string.
   */
  t(key: TranslationKey): string;

  /** Returns the active base locale code */
  getActiveLocale(): SupportedLocale;

  /** Returns true if the active locale is RTL */
  isRTL(): boolean;
}

/** Array of all valid translation keys, useful for iteration and tests */
export const ALL_TRANSLATION_KEYS: TranslationKey[] = [
  "labels.cardNumber",
  "labels.expirationDate",
  "labels.cvc",
  "placeholders.cardNumber",
  "placeholders.expirationDate",
  "placeholders.cvc",
  "errors.invalidCardNumber",
  "errors.invalidExpiryDate",
  "errors.invalidCvc",
  "errors.incompleteCardNumber",
  "errors.incompleteExpiryDate",
  "errors.incompleteCvc",
  "errors.expiredCard",
  "aria.cardNumber",
  "aria.expirationDate",
  "aria.cvc",
  "aria.cardInput",
];

/** Array of all supported locale codes */
export const SUPPORTED_LOCALES: SupportedLocale[] = [
  "en", "fr", "de", "es", "pt", "it", "nl", "ja", "zh", "ar",
  "hi", "bn", "ru", "ko", "vi", "tr", "th", "pl", "uk", "ms", "tl", "sw", "ta", "he", "id",
];

const RTL_LOCALES: ReadonlySet<string> = new Set(["ar", "he"]);

class TranslationManagerImpl implements TranslationManager {
  private activeLocale: SupportedLocale = "en";
  private activeCatalog: TranslationCatalog = catalogs.en;
  private filteredOverrides: Partial<Record<TranslationKey, string>> = {};

  /** Initialise with a locale and optional merchant overrides, falling back to English for unsupported locales. */
  init(locale: string, overrides?: TranslationOverride): void {
    const baseLanguage = locale.split("-")[0].toLowerCase();

    if (SUPPORTED_LOCALES.includes(baseLanguage as SupportedLocale)) {
      this.activeLocale = baseLanguage as SupportedLocale;
      this.activeCatalog = catalogs[this.activeLocale];
    } else {
      console.warn(
        `Locale "${locale}" is not supported. Falling back to English.`,
      );
      this.activeLocale = "en";
      this.activeCatalog = catalogs.en;
    }

    this.filteredOverrides = {};
    if (overrides) {
      for (const key of ALL_TRANSLATION_KEYS) {
        if (key in overrides && typeof overrides[key] === "string") {
          this.filteredOverrides[key] = overrides[key] as string;
        }
      }
    }
  }

  /** Resolve a translation key to a localised string (override → locale catalog → English fallback). */
  t(key: TranslationKey): string {
    const override = this.filteredOverrides[key];
    if (override !== undefined) {
      return override;
    }

    const localeValue = this.activeCatalog[key];
    if (localeValue) {
      return localeValue;
    }

    return catalogs.en[key];
  }

  getActiveLocale(): SupportedLocale {
    return this.activeLocale;
  }

  isRTL(): boolean {
    return RTL_LOCALES.has(this.activeLocale);
  }
}

export function createTranslationManager(): TranslationManager {
  return new TranslationManagerImpl();
}
